import React, { useEffect, useRef, useState } from "react";
import styles from "./TimeWheelPicker.module.css";
import Button from "../common/Button";

const ITEM_HEIGHT = 40;

interface TimeWheelPickerProps {
  initial?: string; // Для выбора и кнопки "Выбрать"
  onSelect?: (hour: number, minute: number) => void;

  value?: string; // Для управления извне
  onChange?: (value: string) => void;
}

const TimeWheelPicker: React.FC<TimeWheelPickerProps> = ({
  initial = "00:00",
  onSelect,
  value,
  onChange,
}) => {
  const controlled = value !== undefined && onChange !== undefined;

  const initialHour = parseInt((controlled ? value : initial).split(":")[0]) || 0;
  const initialMinute = parseInt((controlled ? value : initial).split(":")[1]) || 0;

  const [hour, setHour] = useState(initialHour);
  const [minute, setMinute] = useState(initialMinute);

  const hourRef = useRef<HTMLUListElement>(null);
  const minuteRef = useRef<HTMLUListElement>(null);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const centerScroll = (ref: React.RefObject<HTMLUListElement>, index: number) => {
    ref.current?.scrollTo({ top: index * ITEM_HEIGHT, behavior: "smooth" });
  };

  useEffect(() => {
    centerScroll(hourRef, hour);
    centerScroll(minuteRef, minute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debounce = (
    func: (
      ref: React.RefObject<HTMLUListElement>,
      items: number[],
      setter: (val: number) => void
    ) => void,
    delay: number
  ) => {
    let timer: NodeJS.Timeout;
    return (
      ref: React.RefObject<HTMLUListElement>,
      items: number[],
      setter: (val: number) => void
    ) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(ref, items, setter), delay);
    };
  };

  const handleSnap = (
    ref: React.RefObject<HTMLUListElement>,
    items: number[],
    setter: (val: number) => void
  ) => {
    if (!ref.current) return;
    let index = Math.round(ref.current.scrollTop / ITEM_HEIGHT);
    index = Math.max(0, Math.min(index, items.length - 1));
    setter(items[index]);
  };

  const handleSnapDebounced = debounce(handleSnap, 100);

  // Обновляем value если компонент контролируемый
  useEffect(() => {
    if (controlled) {
      const formatted = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      onChange(formatted);
    }
  }, [hour, minute, controlled, onChange]);

  return (
    <div className={styles.pickerWrapper}>
      <div className={styles.wrapper}>
        <ul
          ref={hourRef}
          className={styles.wheel}
          onScroll={() => handleSnapDebounced(hourRef, hours, setHour)}
        >
          <li></li>
          <li></li>
          {hours.map((h) => (
            <li key={h} className={h === hour ? styles.active : ""}>
              {String(h).padStart(2, "0")}
            </li>
          ))}
          <li></li>
          <li></li>
        </ul>

        <ul
          ref={minuteRef}
          className={styles.wheel}
          onScroll={() => handleSnapDebounced(minuteRef, minutes, setMinute)}
        >
          <li></li>
          <li></li>
          {minutes.map((m) => (
            <li key={m} className={m === minute ? styles.active : ""}>
              {String(m).padStart(2, "0")}
            </li>
          ))}
          <li></li>
          <li></li>
        </ul>

        <div className={styles.colon}>:</div>

        <div className={styles.highlight}></div>
        <div className={styles.fadeTop}></div>
        <div className={styles.fadeBottom}></div>
      </div>

      {!controlled && (
        <Button className={styles.confirmBtn} onClick={() => onSelect?.(hour, minute)}>
          Выбрать
        </Button>
      )}
    </div>
  );
};

export default TimeWheelPicker;
